import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { baseURL } from '../api/config';

interface LpEntity {
  id: number;
  name: string;
  lpType: string;
  region: string;
  isActive: boolean;
}

interface Symbol {
  id: number;
  name: string;
  description: string;
  lpId: number;
  lpEntity: LpEntity;
  lpSymbolName: string;
  digits: number;
  contractSize: number;
  tickSize: number;
  baseCurrency: string;
  quoteCurrency: string;
  isActive: boolean;
  isSubscribed: boolean;
  securityType: string;
}

interface TickStatistics {
  symbol: string;
  tickCount24h: number;
  lastTickTime: string | null;
  status: 'Live' | 'Stale' | 'No Data';
}

interface DataFeedSubscription {
  id: number;
  symbolName: string;
  storeHistoricalData: boolean;
  retentionDays: number | null;
  enableRealtime: boolean;
  createdAt: string;
  updatedAt: string | null;
  createdBy: string | null;
  notes: string | null;
}

const SymbolMapping: React.FC = () => {
  const [symbols, setSymbols] = useState<Symbol[]>([]);
  const [subscriptions, setSubscriptions] = useState<Map<string, DataFeedSubscription>>(new Map());
  const [tickStats, setTickStats] = useState<Map<string, TickStatistics>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [updatingSymbol, setUpdatingSymbol] = useState<string | null>(null);

  const API_BASE = `${baseURL}/api`;

  useEffect(() => {
    loadSymbols();
  }, []);

  const loadSymbols = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch symbols from DataFeed Symbol Proxy (proxies to FixEngine)
      // Production: DataFeedServer on same VPS, FixEngine on different VPS
      // Note: Symbol Proxy endpoint doesn't require authentication in current implementation
      const symbolsRes = await axios.get(`${baseURL}/api/symbols?includeLp=true`);

      setSymbols(symbolsRes.data || []);

      // Fetch subscriptions from DataFeed.Server
      await loadSubscriptions();

      // Fetch tick statistics from DataFeed.Server
      await loadTickStatistics(symbolsRes.data || []);

    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load symbols';
      setError(errorMsg);
      console.error('Failed to load symbols:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No auth token found');
        return;
      }

      const response = await axios.get(`${API_BASE}/subscriptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const subsMap = new Map<string, DataFeedSubscription>();
      response.data.forEach((sub: DataFeedSubscription) => {
        subsMap.set(sub.symbolName, sub);
      });

      setSubscriptions(subsMap);
    } catch (err: any) {
      console.error('Failed to load subscriptions:', err);
    }
  };

  const loadTickStatistics = async (symbolList: Symbol[]) => {
    try {
      // Placeholder for Task A.3
      const statsMap = new Map<string, TickStatistics>();

      symbolList.forEach(symbol => {
        statsMap.set(symbol.name, {
          symbol: symbol.name,
          tickCount24h: 0,
          lastTickTime: null,
          status: 'No Data'
        });
      });

      setTickStats(statsMap);
    } catch (err) {
      console.error('Failed to load tick statistics:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSymbols();
    setRefreshing(false);
  };

  const handleToggleStorage = async (symbol: Symbol) => {
    try {
      setUpdatingSymbol(symbol.name);
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login first');
        return;
      }

      const existingSub = subscriptions.get(symbol.name);

      if (existingSub) {
        // Toggle off - delete subscription
        await axios.delete(`${API_BASE}/subscriptions/${symbol.name}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Remove from local state
        const newSubs = new Map(subscriptions);
        newSubs.delete(symbol.name);
        setSubscriptions(newSubs);
      } else {
        // Toggle on - create subscription
        const newSub = {
          symbolName: symbol.name,
          storeHistoricalData: true,
          retentionDays: 365,
          enableRealtime: true,
          notes: `Auto-created for ${symbol.description}`
        };

        const response = await axios.post(`${API_BASE}/subscriptions`, newSub, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Add to local state
        const newSubs = new Map(subscriptions);
        newSubs.set(symbol.name, response.data);
        setSubscriptions(newSubs);
      }

    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update subscription';
      alert(`Error: ${errorMsg}`);
      console.error('Failed to toggle storage:', err);
    } finally {
      setUpdatingSymbol(null);
    }
  };

  const handleToggleRealtime = async (symbol: Symbol) => {
    try {
      setUpdatingSymbol(symbol.name);
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login first');
        return;
      }

      const existingSub = subscriptions.get(symbol.name);
      if (!existingSub) {
        alert('Please enable storage first');
        return;
      }

      // Toggle realtime setting
      const updatedSub = {
        ...existingSub,
        enableRealtime: !existingSub.enableRealtime
      };

      await axios.put(`${API_BASE}/subscriptions/${symbol.name}`, updatedSub, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Update local state
      const newSubs = new Map(subscriptions);
      newSubs.set(symbol.name, updatedSub);
      setSubscriptions(newSubs);

    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update realtime setting';
      alert(`Error: ${errorMsg}`);
      console.error('Failed to toggle realtime:', err);
    } finally {
      setUpdatingSymbol(null);
    }
  };

  const getStatusBadge = (symbol: Symbol) => {
    const stats = tickStats.get(symbol.name);
    const subscription = subscriptions.get(symbol.name);

    if (!symbol.isActive) {
      return (
        <span style={{
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: '#6c757d',
          color: 'white'
        }}>
          Inactive
        </span>
      );
    }

    if (!symbol.isSubscribed) {
      return (
        <span style={{
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: '#ffc107',
          color: '#000'
        }}>
          Not Subscribed
        </span>
      );
    }

    if (!subscription) {
      return (
        <span style={{
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: '#dc3545',
          color: 'white'
        }}>
          No DataFeed Sub
        </span>
      );
    }

    if (stats?.status === 'Live') {
      return (
        <span style={{
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: '#28a745',
          color: 'white'
        }}>
          Live
        </span>
      );
    }

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: '#17a2b8',
        color: 'white'
      }}>
        Subscribed
      </span>
    );
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <div>Loading symbol mapping...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <h2 style={{ margin: 0, marginBottom: '8px' }}>Symbol Mapping & DataFeed Subscriptions</h2>
            <p style={{ margin: 0, color: '#6c757d', fontSize: '14px' }}>
              View symbol configurations from FixEngine and manage DataFeed storage subscriptions
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              opacity: refreshing ? 0.6 : 1
            }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {/* Symbol Count */}
        <div style={{
          padding: '12px',
          backgroundColor: '#e7f3ff',
          borderRadius: '4px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#004085'
        }}>
          <strong>{symbols.length}</strong> symbols configured | <strong>{subscriptions.size}</strong> DataFeed subscriptions active
        </div>

        {/* Symbols Table */}
        {symbols.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            color: '#6c757d'
          }}>
            No symbols found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: 'white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              borderRadius: '4px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>Symbol</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>LP</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>Storage</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>Realtime</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>Ticks (24h)</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {symbols.map((symbol, index) => {
                  const stats = tickStats.get(symbol.name);
                  const subscription = subscriptions.get(symbol.name);
                  const isUpdating = updatingSymbol === symbol.name;

                  return (
                    <tr
                      key={symbol.id}
                      style={{
                        borderBottom: index < symbols.length - 1 ? '1px solid #dee2e6' : 'none',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                      }}
                    >
                      <td style={{ padding: '12px', fontWeight: '600', fontFamily: 'monospace', color: '#007bff' }}>
                        {symbol.name}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#1f2937' }}>
                        {symbol.description}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#1f2937' }}>
                        {symbol.lpEntity?.name || 'N/A'}
                      </td>

                      {/* Storage Toggle */}
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleToggleStorage(symbol)}
                          disabled={isUpdating || !symbol.isSubscribed}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: subscription ? '#28a745' : '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isUpdating || !symbol.isSubscribed ? 'not-allowed' : 'pointer',
                            opacity: isUpdating || !symbol.isSubscribed ? 0.6 : 1,
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                          title={!symbol.isSubscribed ? 'Symbol not subscribed in FixEngine' : subscription ? 'Click to disable storage' : 'Click to enable storage'}
                        >
                          {isUpdating ? '...' : subscription ? 'ON' : 'OFF'}
                        </button>
                      </td>

                      {/* Realtime Toggle */}
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleToggleRealtime(symbol)}
                          disabled={isUpdating || !subscription}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: subscription?.enableRealtime ? '#28a745' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isUpdating || !subscription ? 'not-allowed' : 'pointer',
                            opacity: isUpdating || !subscription ? 0.6 : 1,
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                          title={!subscription ? 'Enable storage first' : subscription.enableRealtime ? 'Click to disable realtime' : 'Click to enable realtime'}
                        >
                          {isUpdating ? '...' : subscription?.enableRealtime ? 'ON' : 'OFF'}
                        </button>
                      </td>

                      <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace', fontSize: '13px', color: '#1f2937' }}>
                        {stats ? formatNumber(stats.tickCount24h) : '-'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {getStatusBadge(symbol)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Info Footer */}
        <div style={{
          marginTop: '20px',
          padding: '12px',
          backgroundColor: '#d1ecf1',
          borderRadius: '4px',
          fontSize: '13px',
          color: '#0c5460',
          border: '1px solid #bee5eb'
        }}>
          <strong>Two-Layer Subscription System:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li><strong>Layer 1 (FixEngine):</strong> Subscribes to LP and receives ticks (configured in MySQL)</li>
            <li><strong>Layer 2 (DataFeed):</strong> Controls which ticks are stored and broadcast (configured here)</li>
            <li><strong>Storage Toggle:</strong> Enable/disable TimescaleDB historical data storage</li>
            <li><strong>Realtime Toggle:</strong> Enable/disable SignalR live tick broadcasting to dashboards</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default SymbolMapping;
